import { buildProductStatusPatch } from './productStatus.js';

/**
 * Eliminación de productos desde el detalle.
 *
 * Por qué hay dos caminos y no un DELETE a secas: en el esquema, las FK de
 * `sale_items.product_id`, `movimientos_stock.producto_id`, `produccion.producto_id`
 * y `product_combo_items.product_id` tienen delete_rule = NO ACTION. Borrar un
 * producto con historial falla con 23503. Y el ajuste a 0 que pide el negocio
 * inserta una fila en `movimientos_stock` que vuelve a bloquear el borrado del
 * producto que la generó.
 *
 *   - SIN historial → borrado FÍSICO. Las tablas satélite (product_variants,
 *     stock_levels, product_cost_history, recetas, product_combos, …) están en
 *     CASCADE y se limpian solas. No se registran movimientos de ajuste: serían
 *     huérfanos y además impedirían el propio DELETE.
 *   - CON historial → requiere `allow_product_deletion_with_movements`. Se
 *     ajusta el stock a 0 en cada sitio con saldo, se registra un movimiento por
 *     ajuste y se da de baja lógica el producto (archived_at + active=false).
 *     La fila sobrevive para que ventas, kardex y rentabilidad sigan resolviendo;
 *     `sale_items.product_name_snapshot` ya conserva el nombre.
 *
 * Nota sobre depósitos: `stock_levels` lleva saldo por `site_id`, no por
 * depósito (los depósitos son un array de texto en `sitios` y solo se registran
 * en el movimiento). "Ajustar en todas las sucursales y depósitos" son, en la
 * práctica, N ajustes: uno por sitio con saldo.
 */

const MOTIVO_AJUSTE = 'Ajuste por eliminación de producto';

const createDeletionError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Qué puede hacer esta organización, según sus dos lógicos. */
export const getProductDeletionPolicy = (organization) => {
  const canDelete = Boolean(organization?.allow_product_deletion);
  return {
    canDelete,
    // El segundo lógico no sirve solo: sin el primero no hay botón que valga.
    canDeleteWithMovements:
      canDelete && Boolean(organization?.allow_product_deletion_with_movements),
  };
};

const countRows = async (supabase, table, column, productId, organizationId) => {
  const { count, error } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .eq(column, productId)
    .eq('organization_id', organizationId);

  // Una tabla ausente o sin permiso no debe habilitar un borrado destructivo:
  // ante la duda se cuenta como historial (se propaga el error al llamador).
  if (error) throw error;
  return toNumber(count);
};

/**
 * Cuántas recetas de OTROS productos usan a este como ingrediente.
 *
 * `recetas.ingredientes` es jsonb sin FK, así que un DELETE físico no falla:
 * la receta ajena queda apuntando a un uuid inexistente y el costeo se rompe
 * sin error visible. Encima el array convive con dos formas de nombrar la
 * referencia — `id` (la vieja) y `product_id` —, así que hay que buscar las dos.
 * (`recetas.producto_id`, el dueño de la receta, sí tiene FK CASCADE y no es
 * problema: la receta propia se va con el producto.)
 */
const countRecipeUses = async (supabase, productId, organizationId) => {
  const buscarPorClave = async (clave) => {
    // OJO: `.contains()` con un array de JS lo serializa con `.join(',')`, o sea
    // `cs.{[object Object]}`, y Postgres responde 22P02. Para jsonb hay que
    // pasarle el JSON ya serializado como STRING: así viaja `cs.[{"id":"…"}]`.
    const { count, error } = await supabase
      .from('recetas')
      .select('id', { count: 'exact', head: true })
      .contains('ingredientes', JSON.stringify([{ [clave]: productId }]))
      .eq('organization_id', organizationId);

    if (error) throw error;
    return toNumber(count);
  };

  const [porId, porProductId] = await Promise.all([
    buscarPorClave('id'),
    buscarPorClave('product_id'),
  ]);

  return porId + porProductId;
};

/**
 * Radiografía del producto antes de borrarlo: qué lo ata al sistema y cuánto
 * stock habría que ajustar. La UI usa esto para mostrar el impacto real.
 */
export const loadProductDeletionImpact = async ({ supabase, productId, organizationId }) => {
  if (!supabase || !productId || !organizationId) {
    throw createDeletionError('INVALID_ARGS', 'Faltan datos para evaluar el producto');
  }

  const [
    saleItems,
    stockMoves,
    production,
    comboItems,
    comboStepOptions,
    recipeUses,
    creditNotes,
    returns,
    productionRequests,
    productionSteps,
  ] = await Promise.all([
    countRows(supabase, 'sale_items', 'product_id', productId, organizationId),
    countRows(supabase, 'movimientos_stock', 'producto_id', productId, organizationId),
    countRows(supabase, 'produccion', 'producto_id', productId, organizationId),
    countRows(supabase, 'product_combo_items', 'product_id', productId, organizationId),
    // Combos dinámicos (enable_dynamic_combos). La FK es CASCADE: sin contarlo,
    // borrar el producto lo sacaría en silencio del combo de OTRO producto.
    countRows(supabase, 'product_combo_step_options', 'product_id', productId, organizationId),
    countRecipeUses(supabase, productId, organizationId),
    // Las cuatro siguientes tienen FK ON DELETE SET NULL: el borrado no falla,
    // simplemente les anula la referencia y el comprobante pierde el producto.
    countRows(supabase, 'credit_note_items', 'product_id', productId, organizationId),
    countRows(supabase, 'return_items', 'product_id', productId, organizationId),
    countRows(supabase, 'production_request_lines', 'product_id', productId, organizationId),
    countRows(supabase, 'production_step_entries', 'product_id', productId, organizationId),
  ]);

  const comboUses = comboItems + comboStepOptions;
  // Notas de crédito y devoluciones son documentos de venta: van con las ventas.
  const sales = saleItems + creditNotes + returns;
  // Pedidos y partes de producción, con la producción.
  const productionTotal = production + productionRequests + productionSteps;

  const { data: levels, error: levelsError } = await supabase
    .from('stock_levels')
    .select('id, site_id, quantity')
    .eq('product_id', productId)
    .eq('organization_id', organizationId);

  if (levelsError) throw levelsError;

  const stockLevels = (levels || []).map((row) => ({
    id: row.id,
    site_id: row.site_id,
    quantity: toNumber(row.quantity),
  }));

  const counts = {
    sales,
    stockMoves,
    production: productionTotal,
    comboUses,
    recipeUses,
  };

  return {
    counts,
    // Stock cargado pero nunca movido NO es historial: ese producto se borra de
    // verdad. Lo que ata al producto es haberse vendido, movido, producido,
    // formar parte de un combo o ser ingrediente de la receta de otro.
    hasHistory:
      sales > 0 ||
      stockMoves > 0 ||
      productionTotal > 0 ||
      comboUses > 0 ||
      recipeUses > 0,
    stockLevels,
    totalUnits: stockLevels.reduce((acc, row) => acc + row.quantity, 0),
    sitesWithStock: stockLevels.filter((row) => row.quantity !== 0).length,
  };
};

/**
 * Lleva a 0 cada sitio con saldo y deja el rastro en el kardex.
 *
 * El movimiento se inserta ANTES de tocar el saldo, a propósito. Esto no corre
 * dentro de una transacción (son llamadas sueltas de PostgREST), así que hay que
 * elegir qué queda si falla a mitad: un movimiento registrado sin ajuste aplicado
 * es un descuadre visible entre kardex y `stock_levels`, detectable y corregible;
 * un saldo puesto en 0 sin movimiento es stock que desaparece sin rastro y no lo
 * repara ni el reintento, porque al recalcular ya ve el saldo en cero.
 * Pendiente de mejora: mover todo esto a una RPC transaccional.
 */
const ajustarStockACero = async ({ supabase, productId, organizationId, stockLevels }) => {
  const pendientes = stockLevels.filter((row) => row.quantity !== 0);
  if (!pendientes.length) return 0;

  const ahora = new Date().toISOString();

  for (const level of pendientes) {
    // La cantidad del movimiento siempre es positiva; el signo lo da el tipo.
    // Saldo positivo → sale del sitio (Egreso). Saldo negativo → entra para
    // volver a cero (Ingreso).
    const esEgreso = level.quantity > 0;
    const { error: movError } = await supabase.from('movimientos_stock').insert([
      {
        organization_id: organizationId,
        producto_id: productId,
        tipo: esEgreso ? 'Egreso' : 'Ingreso',
        cantidad: Math.abs(level.quantity),
        motivo: MOTIVO_AJUSTE,
        sitio_origen: esEgreso ? level.site_id : null,
        sitio_destino: esEgreso ? null : level.site_id,
      },
    ]);

    if (movError) throw movError;

    const { error: updateError } = await supabase
      .from('stock_levels')
      .update({ quantity: 0, updated_at: ahora })
      .eq('id', level.id)
      .eq('organization_id', organizationId);

    if (updateError) throw updateError;
  }

  return pendientes.length;
};

/**
 * Elimina un producto respetando la política de la organización.
 *
 * Devuelve `{ mode: 'hard' | 'archived', adjustments, archivedAt }`.
 * Lanza Error con `.code`: DELETION_DISABLED | HAS_HISTORY | INVALID_ARGS.
 */
export const deleteProduct = async ({
  supabase,
  product,
  organization,
  organizationId,
}) => {
  if (!supabase || !product?.id || !organizationId) {
    throw createDeletionError('INVALID_ARGS', 'Faltan datos para eliminar el producto');
  }

  const policy = getProductDeletionPolicy(organization);
  if (!policy.canDelete) {
    throw createDeletionError(
      'DELETION_DISABLED',
      'La eliminación de productos no está habilitada para esta empresa',
    );
  }

  // Siempre se recalcula, aunque la UI ya haya pedido el impacto para pintar la
  // confirmación. Entre que se abre el panel y se confirma puede haber pasado
  // una venta: reusar aquel snapshot registraría el movimiento con la cantidad
  // vieja y, tras un fallo parcial, un reintento duplicaría los movimientos.
  const evaluado = await loadProductDeletionImpact({
    supabase,
    productId: product.id,
    organizationId,
  });

  if (evaluado.hasHistory && !policy.canDeleteWithMovements) {
    throw createDeletionError(
      'HAS_HISTORY',
      'El producto tiene movimientos asociados y la empresa no tiene habilitada esa opción',
    );
  }

  if (!evaluado.hasHistory) {
    // Sin historial: borrado real. Doble candado id + organización para no
    // depender solo de RLS.
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', product.id)
      .eq('organization_id', organizationId);

    if (error) throw error;
    return { mode: 'hard', adjustments: 0, archivedAt: null };
  }

  const adjustments = await ajustarStockACero({
    supabase,
    productId: product.id,
    organizationId,
    stockLevels: evaluado.stockLevels,
  });

  const archivedAt = new Date().toISOString();
  const { error: archiveError } = await supabase
    .from('products')
    .update({ ...buildProductStatusPatch(false), archived_at: archivedAt })
    .eq('id', product.id)
    .eq('organization_id', organizationId);

  if (archiveError) throw archiveError;

  return { mode: 'archived', adjustments, archivedAt };
};
